import { Component, OnDestroy, OnInit } from '@angular/core';
import { NewImportExportService } from '../../../_service/new-import-export.service';
import { Subscription } from 'rxjs';
import { User } from 'src/app/authentification/signin/credentials';
import { SigninService } from 'src/app/authentification/signin/signin.service';

@Component({
  selector: 'app-fueling-excel',
  templateUrl: './fueling-excel.component.html',
  styleUrls: ['./fueling-excel.component.css'],
})
export class FuelingExcelComponent implements OnInit, OnDestroy {
  excelData: any[][] | null = null;
  excelData$: Subscription;
  current: string = '';
  public currentUser: User = new User();
  selectedRowIndex: number | null = null;

  // Entêtes "utiles" (ligne 4 de l'Excel), sans les colonnes vides en fin de ligne (H, I ...)
  headerColumns: any[] = [];

  // Lignes de données (hors bandeau, hors entêtes, hors "Total" et "Moyenne (DRM)")
  dataRows: any[][] = [];
  // Ligne "Total" (le mot "Total" est dans la colonne "Véhicule", pas la 1ère colonne)
  totalRow: any[] | null = null;
  // Ligne "Moyenne (DRM)" affichée après le Total (séparée par une ligne vide dans le fichier)
  moyenneRow: any[] | null = null;

  // A partir de cet index (dans headerColumns), les colonnes sont numériques
  // (Coût Total, Qté Total) -> pour l'alignement + le format
  numericStartIndex = 5;

  constructor(
    private importExportService: NewImportExportService,
    private authService: SigninService
  ) {
    const storedUser = localStorage.getItem('currentUser');
    this.currentUser = storedUser ? JSON.parse(storedUser) : {};
  }

  ngOnInit(): void {
    this.excelData$ = this.importExportService
      .getExcelData()
      .subscribe((value) => {
        if (
          value != null &&
          Array.isArray(value) &&
          value.length > 4 &&
          value.every((row) => Array.isArray(row)) &&
          Array.isArray(value[3]) &&
          value[3].length > 0
        ) {
          this.excelData = value as any[][];
          this.computeHeaderColumns();
          this.extractRows();
          this.selectedRowIndex = null;
        } else {
          this.excelData = null;
          this.headerColumns = [];
          this.dataRows = [];
          this.totalRow = null;
          this.moyenneRow = null;
        }
      });
    this.fetchUser();
  }

  fetchUser(): void {
    this.current = this.authService.getCurrentUser().displayName;
  }

  ngOnDestroy(): void {
    if (this.excelData$) {
      this.excelData$.unsubscribe();
    }
    this.importExportService.setExcelData(null);
    this.importExportService.setCurentRapport(null);
    this.importExportService.setPdfToBePrinted(null);
    this.importExportService.setExcelToBeProcessed(null);
    this.selectedRowIndex = null;
  }

  selectRow(index: number): void {
    this.selectedRowIndex = index;
  }

  /**
   * Retire les cellules vides/null en fin de ligne (colonnes techniques H, I... non utilisées
   * dans ce rapport) pour obtenir le nombre réel de colonnes utiles.
   */
  private trimRow(row: any[]): any[] {
    const copy = [...(row ?? [])];
    while (
      copy.length &&
      (copy[copy.length - 1] === null ||
        copy[copy.length - 1] === undefined ||
        copy[copy.length - 1] === '')
    ) {
      copy.pop();
    }
    return copy;
  }

  private isBlankRow(row: any[]): boolean {
    return row.every(
      (cell) => cell === null || cell === undefined || cell === ''
    );
  }

  private computeHeaderColumns(): void {
    this.headerColumns = this.trimRow(this.excelData?.[3] ?? []);
  }

  /**
   * Sépare le tableau brut renvoyé par le service en :
   * - dataRows  : les lignes de données (à partir de la ligne 5 du fichier Excel)
   * - totalRow  : la ligne contenant "Total" dans une de ses cellules
   * - moyenneRow: la ligne "Moyenne (DRM)" qui suit le Total (après une ligne vide)
   */
  private extractRows(): void {
    if (!this.excelData || this.excelData.length <= 4 || !this.headerColumns.length) {
      this.dataRows = [];
      this.totalRow = null;
      this.moyenneRow = null;
      return;
    }

    const numCols = this.headerColumns.length;
    const rows = this.excelData
      .slice(4)
      .map((row) => this.trimRow((row ?? []).slice(0, numCols)));

    const totalIndex = rows.findIndex((row) =>
      row.some(
        (cell) =>
          typeof cell === 'string' && cell.trim().toLowerCase() === 'total'
      )
    );

    if (totalIndex === -1) {
      this.dataRows = rows.filter((row) => !this.isBlankRow(row));
      this.totalRow = null;
      this.moyenneRow = null;
      return;
    }

    this.dataRows = rows.slice(0, totalIndex).filter((row) => !this.isBlankRow(row));
    this.totalRow = rows[totalIndex];

    this.moyenneRow =
      rows
        .slice(totalIndex + 1)
        .find(
          (row) =>
            !this.isBlankRow(row) &&
            row.some(
              (cell) =>
                typeof cell === 'string' &&
                cell.trim().toLowerCase().startsWith('moyenne')
            )
        ) ?? null;
  }

  isNumeric(colIndex: number): boolean {
    return colIndex >= this.numericStartIndex;
  }
}